/**
 * InterestPickerScreen
 *
 * Full-screen, one-time interest picker shown to new visitors.
 * Inspired by X/Twitter's "Pick your interests" onboarding.
 *
 * - Shows only when `hasSeenPicker` is false (first browser session)
 * - Uses SightTrails theme: white bg, #2A7ECE (light blue), #1E63A8 (dark blue)
 * - Categories loaded live from backend (same call the splash already made)
 * - Does NOT require authentication or an account
 * - Does NOT alter any existing data
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserInterests } from '../../contexts/UserInterestsContext';
import { categoryService } from '../../services/categoryService';
import logoImage from '../../assets/logo.png';

// Icons per category — fallback to a safari emoji if no match
const CATEGORY_ICONS: Record<string, string> = {
  adventure: '🧗',
  luxury: '✨',
  cultural: '🎭',
  family: '👨‍👩‍👧',
  migration: '🦁',
  wildlife: '🐘',
  photography: '📸',
  honeymoon: '💑',
  'water sports': '🏄',
  spiritual: '🕊️',
  conservation: '🌿',
  wellness: '🧘',
  history: '🏛️',
  beach: '🏖️',
  all: '🌍',
};

const getIcon = (name: string) =>
  CATEGORY_ICONS[name.toLowerCase()] || '🌍';

interface InterestPickerScreenProps {
  children: React.ReactNode;
}

const InterestPickerScreen: React.FC<InterestPickerScreenProps> = ({ children }) => {
  const { hasSeenPicker, saveInterests, skipPicker } = useUserInterests();
  const [categories, setCategories] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  // Load categories; show picker if this is a first visit
  useEffect(() => {
    if (hasSeenPicker) return;

    const load = async () => {
      try {
        const cats = await categoryService.getActiveCategories();
        const names = cats.map((c: any) => c.name).filter(Boolean);
        setCategories(names.length > 0 ? names : ['Adventure', 'Luxury', 'Cultural', 'Family', 'Migration', 'Wildlife', 'Photography', 'Honeymoon']);
      } catch {
        // Fallback list if backend is unreachable
        setCategories(['Adventure', 'Luxury', 'Cultural', 'Family', 'Migration', 'Wildlife', 'Photography', 'Honeymoon']);
      }
      setIsVisible(true);
    };

    load();
  }, [hasSeenPicker]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleContinue = () => {
    setIsDismissing(true);
    setTimeout(() => {
      saveInterests(Array.from(selected));
      setIsVisible(false);
    }, 450);
  };

  const handleSkip = () => {
    setIsDismissing(true);
    setTimeout(() => {
      skipPicker();
      setIsVisible(false);
    }, 300);
  };

  // If already seen, render children directly
  if (hasSeenPicker) return <>{children}</>;

  return (
    <>
      {/* Render children in background (hidden) so they're ready */}
      <div className="pointer-events-none opacity-0 select-none" aria-hidden>
        {children}
      </div>

      <AnimatePresence>
        {isVisible && (
          <motion.div
            key="interest-picker"
            initial={{ opacity: 0 }}
            animate={{ opacity: isDismissing ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[100000] bg-white flex flex-col items-center px-4 sm:px-6 py-8 sm:py-14 overflow-y-auto"
          >
            {/* Top: Logo */}
            <div className="w-full flex flex-col items-center gap-2 mb-6 sm:mb-8 flex-shrink-0">
              <img src={logoImage} alt="SightTrails" className="h-10 sm:h-12 w-auto object-contain" />
            </div>

            {/* Centre: Heading + chips */}
            <div className="flex-1 flex flex-col items-center justify-center w-full max-w-2xl mx-auto">
              <h1 className="text-xl sm:text-3xl font-extrabold text-slate-900 text-center mb-3 leading-tight px-2">
                What kind of safari<br className="hidden sm:block" /> calls to you?
              </h1>
              <p className="text-xs sm:text-sm text-slate-400 text-center mb-8 font-medium px-4">
                {selected.size === 0
                  ? 'Select at least one to personalise your experience'
                  : `${selected.size} selected — you can always change this later`}
              </p>

              {/* Chip grid — wraps naturally like X */}
              <div className="flex flex-wrap gap-2.5 sm:gap-3 justify-center w-full">
                {categories.map((name) => {
                  const isSelected = selected.has(name);
                  return (
                    <motion.button
                      key={name}
                      whileTap={{ scale: 0.93 }}
                      onClick={() => toggle(name)}
                      className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full border-2 font-semibold text-xs sm:text-sm transition-all duration-200 select-none ${
                        isSelected
                          ? 'bg-[#2A7ECE] border-[#2A7ECE] text-white shadow-md shadow-blue-200'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-[#2A7ECE] hover:text-[#2A7ECE]'
                      }`}
                    >
                      <span className="text-sm sm:text-base leading-none">{getIcon(name)}</span>
                      <span>{name}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Bottom: Continue + Skip */}
            <div className="flex flex-col items-center gap-3 w-full max-w-[280px] sm:max-w-sm mt-10 mb-4 flex-shrink-0">
              <motion.button
                whileTap={{ scale: 0.97 }}
                disabled={selected.size === 0}
                onClick={handleContinue}
                className={`w-full py-3.5 sm:py-4 rounded-full font-bold text-sm sm:text-base transition-all duration-300 ${
                  selected.size > 0
                    ? 'bg-[#1E63A8] text-white shadow-xl shadow-blue-200 hover:bg-[#2A7ECE]'
                    : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                }`}
              >
                Continue
              </motion.button>
              <button
                onClick={handleSkip}
                className="text-[10px] sm:text-xs text-slate-400 hover:text-slate-600 transition-colors font-medium py-2 uppercase tracking-widest"
              >
                Skip for now
              </button>
            </div>
          </motion.div>

        )}
      </AnimatePresence>
    </>
  );
};

export default InterestPickerScreen;
